import {
  GetTerminalResponseFromJSON,
  type GetTerminalResponse,
} from "@/openapi/generated/models";
import { formatCurrencyAmount } from "@/lib/utils/currency";

/** Fallback when validate succeeds with no response body (e.g. 204 / empty 200). */
export const EMPTY_TERMINAL_SURCHARGE_RESPONSE: GetTerminalResponse = {
  terminalId: "",
  company: null,
  enabled: false,
  surchargeEnabled: false,
  surchargePercentage: 0,
  surchargeDisclosure: [],
};

/**
 * Parses the validate endpoint body. The API may return an empty body when no
 * surcharge disclosure is configured, which breaks the generated client's json().
 */
export function parseSurchargeDisclosureValidateResponseBody(
  body: string,
): GetTerminalResponse {
  const trimmed = body.trim();
  if (!trimmed) {
    return EMPTY_TERMINAL_SURCHARGE_RESPONSE;
  }

  const json = JSON.parse(trimmed) as unknown;
  if (
    json &&
    typeof json === "object" &&
    "data" in json &&
    (json as { data?: unknown }).data
  ) {
    return GetTerminalResponseFromJSON((json as { data: unknown }).data);
  }

  return GetTerminalResponseFromJSON(json);
}

/**
 * Joins non-empty surcharge disclosure lines from the terminal validate response.
 * Returns null when no disclosure text is configured.
 */
export function formatSurchargeDisclosureText(
  disclosure: string[] | null | undefined,
): string | null {
  if (!disclosure?.length) return null;

  const text = disclosure
    .map((line) => line?.trim())
    .filter((line): line is string => Boolean(line))
    .join("\n\n");

  return text.length > 0 ? text : null;
}

export interface SurchargePaymentAmounts {
  baseAmount: number;
  surchargeAmount: number;
  totalAmount: number;
}

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Calculates surcharge and total from the payment amount and terminal settings. */
export function calculateSurchargePaymentAmounts(
  baseAmount: number,
  terminal: Pick<
    GetTerminalResponse,
    "surchargeEnabled" | "surchargePercentage"
  >,
): SurchargePaymentAmounts {
  const amount = Math.max(0, Number(baseAmount) || 0);
  const percentage = Math.max(0, Number(terminal.surchargePercentage) || 0);

  if (!terminal.surchargeEnabled || percentage <= 0 || amount <= 0) {
    return {
      baseAmount: amount,
      surchargeAmount: 0,
      totalAmount: amount,
    };
  }

  const surchargeAmount = roundMoney((amount * percentage) / 100);

  return {
    baseAmount: amount,
    surchargeAmount,
    totalAmount: roundMoney(amount + surchargeAmount),
  };
}

export function formatSurchargePaymentBreakdown(
  amounts: SurchargePaymentAmounts,
  currency: string | number | null | undefined,
  surchargePercentage: number,
): string {
  const formattedBase = formatCurrencyAmount(amounts.baseAmount, currency);
  const formattedSurcharge = formatCurrencyAmount(
    amounts.surchargeAmount,
    currency,
  );
  const formattedTotal = formatCurrencyAmount(amounts.totalAmount, currency);

  if (amounts.surchargeAmount > 0) {
    const percentageLabel = Number.isInteger(surchargePercentage)
      ? `${surchargePercentage}%`
      : `${surchargePercentage.toFixed(2).replace(/\.?0+$/, "")}%`;

    return [
      "Payment summary",
      "",
      `Payment amount: ${formattedBase}`,
      `Credit card surcharge (${percentageLabel}): ${formattedSurcharge}`,
      `Total amount to be charged: ${formattedTotal}`,
    ].join("\n");
  }

  return ["Payment summary", "", `Payment amount: ${formattedBase}`].join("\n");
}

export function buildSurchargeDisclosureDisplayText({
  disclosure,
  amounts,
  currency,
  surchargePercentage,
}: {
  disclosure: string;
  amounts: SurchargePaymentAmounts;
  currency: string | number | null | undefined;
  surchargePercentage: number;
}): string {
  const summary = formatSurchargePaymentBreakdown(
    amounts,
    currency,
    surchargePercentage,
  );

  return `${disclosure}\n\n${summary}`;
}

export const SURCHARGE_DISCLOSURE_REVIEW_PROMPT =
  "Please review the disclosure below before your payment is submitted.";

export function buildSurchargeChargeSummaryText(
  amounts: SurchargePaymentAmounts,
  currency: string | number | null | undefined,
): string | null {
  if (amounts.surchargeAmount <= 0) {
    return null;
  }

  return `Your card will be charged ${formatCurrencyAmount(
    amounts.totalAmount,
    currency,
  )}, including a ${formatCurrencyAmount(
    amounts.surchargeAmount,
    currency,
  )} credit card surcharge.`;
}
